import { useEffect, useRef, useState } from 'react';
import { Button } from '../components/Button';
import { Body, Screen, Title } from '../components/Screen';
import { extensionFor, type Clip } from '../capture';
import { copy } from '../copy';
import { TitleBackdrop } from '../components/TitleBackdrop';
import { links, release, titleLayers } from '../assets';
import type { AppConfig } from '../config';
import { noteEvent } from '../lib/diagnostics';
import { saveBlob } from '../lib/saveClip';
import { posterFromClip } from '../lib/posterFrame';

/**
 * Shared: web step 10-11, kiosk step 6.
 *
 * Playback is always local — the blob is held in memory and shown from an
 * object URL created by useActivation. Nothing round-trips to a server before
 * the guest sees themselves.
 *
 * The two modes differ only in what comes after: web offers the native share
 * sheet or a save, kiosk hands off to phone entry. Same screen, same layout.
 */
export function ResultScreen({
  config,
  clip,
  clipUrl,
  micGranted,
  onRetake,
  onNext,
}: {
  config: AppConfig;
  clip: Clip | null;
  clipUrl: string | null;
  micGranted: boolean;
  onRetake: () => void;
  onNext: () => void;
}) {
  const kiosk = config.mode === 'kiosk';
  /**
   * Transient confirmation after a save.
   *
   * Held as an object rather than a bare string so that saving twice is a new
   * value each time — setting identical string state is a no-op, the effect
   * below would not re-run, and the second toast would inherit the first one's
   * already-running timer and vanish early.
   */
  const [toast, setToast] = useState<{ text: string } | null>(null);

  /**
   * Make the clip actually play.
   *
   * With sound, `autoPlay` is refused outright by iOS Safari — prior taps
   * elsewhere on the page do not count, it wants a gesture on the element. The
   * guest was left looking at a frozen frame with `controls={false}`, i.e. no
   * way to play it at all, which reads as a still thumbnail rather than a
   * video. ClipScreen already learned this and starts muted.
   *
   * So: try with sound, and if that is refused, mute and try again rather than
   * leave a dead frame. Controls only appear on that fallback, so the guest has
   * a way to unmute — the caw is the point — without putting a control bar over
   * the clean preview in the common case.
   */
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [fellBack, setFellBack] = useState(false);

  /**
   * Cover image: a frame from near the end, so it shows the costume rather than
   * the guest's own face at frame 0.
   *
   * Seeded from the clip itself, because the recorder took this picture off the
   * capture canvas as it stopped. It has to be here in the FIRST render, not in
   * the effect below — an effect runs after paint, and that paint is exactly the
   * one that was showing a full-screen grey rectangle while the WebView opened
   * a decoder for the file. `posterFromClip` stays as the fallback.
   */
  const [poster, setPoster] = useState<string | null>(clip?.posterUrl ?? null);

  // Clears itself. Re-armed on every new toast object.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!clip) {
      setPoster(null);
      return;
    }
    // The recorder already handed one over; opening a decoder to find the same
    // frame again would cost a second and change nothing.
    if (clip.posterUrl) {
      setPoster(clip.posterUrl);
      return;
    }
    let cancelled = false;
    void posterFromClip(clip.blob, clip.durationMs).then((url) => {
      if (!cancelled && url) setPoster(url);
    });
    return () => {
      cancelled = true;
    };
  }, [clip]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !clipUrl) return;
    let cancelled = false;

    void (async () => {
      try {
        await video.play();
      } catch {
        if (cancelled) return;
        video.muted = true;
        setFellBack(true);
        try {
          await video.play();
        } catch {
          /* Controls are showing by now; the guest can hit play. */
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clipUrl]);

  /**
   * Both buttons route through the share sheet where the browser can share
   * files, because on iOS that is the only thing that reaches the camera roll.
   * This used to build its own anchor and click it without appending it to the
   * document, which Safari ignores outright — the button did nothing at all.
   */
  async function saveDirect() {
    if (!clip || !clipUrl) return;
    noteEvent('save attempted');
    const outcome = await saveBlob(clip.blob, filenameFor(clip), clip.mimeType);
    // Only say where it went when it actually went to downloads. Saying it
    // unconditionally told guests who had just used the share sheet, and
    // guests who had cancelled, to go looking somewhere it is not.
    if (outcome === 'downloaded') setToast({ text: copy.result.savedToDownloads });
  }

  const actions = (
    <>
      <Button
        variant="red"
        full={false}
        testId="result-save"
        onClick={() => void saveDirect()}
        disabled={!clip}
      >
        {copy.result.save}
      </Button>
      <Button
        variant="sand"
        full={false}
        testId="result-retake"
        onClick={onRetake}
      >
        {copy.result.retake}
      </Button>
    </>
  );

  /**
   * The kiosk panel is 1080px wide, which is WIDER than the `lg` breakpoint —
   * so every `lg:` class written for the web desktop layout also matches on the
   * kiosk. Sharing one clip element between the two meant `lg:h-[80vh]` applied
   * there too: 80% of 1920 is 1536px, and the clip overflowed its band in both
   * directions, straight over the heading above it and the buttons below.
   *
   * Anything shared between web and kiosk has to be checked for that, because
   * `sm`, `md` and `lg` all match on the panel. Only `xl` (1280) does not.
   */
  const clipClass = [
    'aspect-[9/16] h-full max-h-full w-auto max-w-full rounded-2xl bg-black object-cover',
    kiosk ? '' : 'lg:h-[80vh] lg:max-h-none',
  ].join(' ');

  const clipBox = clipUrl ? (
    <video
      ref={videoRef}
      src={clipUrl}
      poster={poster ?? undefined}
      autoPlay
      loop
      muted={!micGranted}
      playsInline
      controls={fellBack}
      className={clipClass}
    />
  ) : (
    <div className="flex aspect-[9/16] h-full max-h-full w-auto max-w-full items-center justify-center rounded-2xl border-2 border-dashed border-current px-6 text-center opacity-50">
      {copy.result.noClip}
    </div>
  );

  // Kiosk is untouched: portrait panel, heading over the clip, one primary
  // action through to phone entry.
  if (kiosk) {
    return (
      // band="media": the clip is the whole point of this screen, and the
      // standard reach band left it 335px wide on a 1080px panel.
      <Screen kiosk band="media" testId="result">
        <div className="pt-4 text-center">
          <Title kiosk>{copy.result.headingKiosk}</Title>
          <Body kiosk>{copy.result.subKiosk}</Body>
        </div>
        {/*
          py-3 rather than py-6, and no min-h floor competing with the flex
          basis: every pixel this band gives up is 9/16 of a pixel off the
          clip's width, so the gaps around it are kept tight on purpose.
        */}
        <div className="flex w-full min-h-48 flex-1 items-center justify-center py-3">{clipBox}</div>
        <div className="w-full max-w-xl space-y-3 pb-4">
          <Button kiosk testId="result-send" onClick={onNext} disabled={!clip}>
            {copy.result.sendToPhone}
          </Button>
          <Button variant="ghost" kiosk testId="result-retake" onClick={onRetake}>
            {copy.result.retake}
          </Button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen backdrop={<ResultBackdrop />} testId="result">
      {/*
        The ground is dark below lg and the key art above it, so the type colour
        has to follow. Title and Body render in `text-current`, so setting it
        once here carries to everything inside.
      */}
      <div className="flex min-h-0 w-full flex-1 flex-col text-bone lg:text-ink">
        {/* Phone: heading over the clip. Replaced by the campaign column on desktop. */}
        <div className="pt-8 text-center lg:hidden">
          <Title>{copy.result.heading}</Title>
          <Body>{copy.result.sub}</Body>
        </div>

        {/*
          Desktop left column: lockup, release line, tickets. Sits above the art,
          which TitleBackdrop places in the lower half of this side.
        */}
        {/*
          Sized in vw and pulled back over Screen's px-6, so the column spans
          0-60vw and its axis lands on exactly 30vw — the same axis
          TitleBackdrop puts the cast on in its `left` placement. A plain `60%`
          is 60% of the PADDED box, which put the copy 10px off the figures.
        */}
        <div className="hidden lg:-ml-6 lg:flex lg:w-[60vw] lg:flex-col lg:items-center lg:gap-5 lg:pt-[7%] lg:text-center">
          <div className="w-full max-w-xl space-y-2">
            <img
              src={titleLayers.wordmark.fallback}
              alt="The Incomer"
              className="mx-auto w-[28rem] select-none"
            />
            <p className="text-base font-bold uppercase tracking-widest text-ink/70">
              {release.line}
            </p>
          </div>
          <a href={links.tickets} target="_blank" rel="noreferrer" className="block w-full max-w-sm">
            <Button onArt testId="result-tickets">
              {copy.endCard.tickets}
            </Button>
          </a>
        </div>

        {/*
          Clip and its actions are one column, which is already the phone layout
          — clip, then buttons under it. On desktop the same column is pulled
          out to the right edge of the window. `right-0` sits flush because an
          absolutely positioned box resolves against its ancestor's padding box,
          so Screen's horizontal padding does not inset it.

          The column takes its width from the clip (the widest child), so the
          button row underneath lines up with the clip's edges rather than
          needing a width of its own.
        */}
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-4 py-6 lg:absolute lg:right-0 lg:top-0 lg:h-full lg:w-auto lg:max-w-[46vw] lg:flex-none lg:gap-5 lg:py-8 lg:pr-8">
          {/*
            The clip needs a box that can shrink. Sharing one centred column
            with the actions and asking for h-full meant the column overflowed,
            and a centred flex container spills BOTH ways — so the top of the
            clip pushed up over the heading. flex-1 with min-h-0 lets it give up
            height to the buttons instead; min-h-48 stops it collapsing.
          */}
          <div className="flex min-h-48 w-full flex-1 items-center justify-center lg:min-h-0 lg:w-auto lg:flex-none">
            {clipBox}
          </div>

          {/*
            Rendered once and repositioned, never duplicated behind a lg:hidden
            pair — two copies would put every data-testid in the DOM twice and
            make a Playwright locator ambiguous.

            Stacked on a phone, a single row under the clip on desktop.
          */}
          <div className="relative w-full max-w-sm lg:max-w-none">
            <div className="flex flex-col gap-3 [&>button]:w-full lg:flex-row lg:items-center lg:gap-3 lg:[&>button]:w-auto lg:[&>button]:flex-1">
              {actions}
            </div>
            {/*
              Floated above the actions rather than placed in flow, so it cannot
              shove the buttons down as it appears and back up as it clears.
              Dark pill because it has to read on the phone's dark layout and
              over the artwork on desktop alike.
            */}
            {toast ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-full mb-3 flex justify-center">
                <p className="rounded-full bg-ink/90 px-4 py-2 text-center text-xs text-bone shadow-lg">
                  {toast.text}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Screen>
  );
}

/**
 * Dark below lg, the key art above it — so the phone result screen is unchanged
 * and only the desktop layout moves onto the campaign composition.
 */
function ResultBackdrop() {
  return (
    <>
      <div className="absolute inset-0 bg-ink lg:hidden" />
      <div className="absolute inset-0 hidden lg:block">
        <TitleBackdrop align="left" />
      </div>
    </>
  );
}

/** How long the save confirmation stays up. */
const TOAST_MS = 4000;

function filenameFor(clip: Clip): string {
  return `the-incomer.${extensionFor(clip.mimeType)}`;
}
